import React, { useMemo } from 'react';
import ModernSelect, {
  ModernSelectOption,
} from 'src/components/ModernSelect';
import { useBranding } from 'src/branding/BrandingContextProvider';
import { filterAllowedStatus } from '../helpers/allowedStatus';
import { useStatusLabelResolver } from 'src/utils/serviceRequestStatus/useStatusLabelResolver';
import './BulkUpdateServiceRequest.scss';

const statuses = [
  { label: 'Submitted', key: 'Submitted', id: 'submitted' },
  { label: 'Scheduled', key: 'Scheduled', id: 'scheduled' },
  { label: 'En Route', key: 'En Route', id: 'enRoute' },
  { label: 'On Hold', key: 'On Hold', id: 'onHold' },
  { label: 'In Progress', key: 'In Progress', id: 'inProgress' },
  { label: 'Resolved', key: 'Resolved', id: 'resolved' },
  { label: 'Declined', key: 'Declined', id: 'declined' },
  { label: 'Closed', key: 'Closed', id: 'closed' },
  { label: 'Assigned', key: 'Assigned', id: 'assigned' },
  { label: 'Under Review', key: 'Under Review', id: 'underReview' },
  { label: 'Approved', key: 'Approved', id: 'approved' },
  {
    label: 'One Year Warranty',
    key: 'One Year Warranty',
    id: 'oneYearWarranty',
  },
];

const SCHEDULED_KEY = 'Scheduled';

interface Option extends ModernSelectOption {
  isDisabled?: boolean;
  disabledReason?: string;
}

const OptionLabel = ({
  label,
  disabledReason,
}: {
  label: string;
  disabledReason?: string;
}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
    }}
  >
    <span>{label}</span>
    {disabledReason && (
      <small
        style={{
          fontStyle: 'italic',
          opacity: 0.6,
          fontSize: '11px',
          marginLeft: '10px',
        }}
      >
        {disabledReason}
      </small>
    )}
  </div>
);

interface BulkUpdateServiceRequestProps {
  disableBulkUpdate: boolean;
  onChangeStatus: (status: string) => void;
  style?: React.CSSProperties;
  label?: string;
  selectedCount?: number;
}

export const BulkUpdateServiceRequest = ({
  disableBulkUpdate,
  onChangeStatus,
  style,
  label = 'UPDATE',
  selectedCount = 0,
}: BulkUpdateServiceRequestProps) => {
  const { branding } = useBranding();
  const resolveStatusLabel = useStatusLabelResolver();

  const options: Option[] = useMemo(() => {
    return filterAllowedStatus(statuses, branding)
      .filter((stat) => !(stat.key === SCHEDULED_KEY && selectedCount > 1))
      .map((stat) => ({
        value: stat.label,
        label: resolveStatusLabel(stat.label),
      }));
  }, [branding, selectedCount, resolveStatusLabel]);

  return (
    <div className="bulkUpdateServReq" style={style}>
      <ModernSelect<Option>
        size="regular"
        options={options}
        isDisabled={disableBulkUpdate}
        isSearchable={false}
        value={null}
        placeholder={label}
        onChange={(option) => {
          if (option) onChangeStatus(option.value);
        }}
        isOptionDisabled={(option) => !!option.isDisabled}
        formatOptionLabel={(option) => (
          <OptionLabel
            label={option.label}
            disabledReason={option.disabledReason}
          />
        )}
      />
    </div>
  );
};
